import React from 'react';

/**
 * Decodes HTML entities to their corresponding characters.
 * Uses a combination of DOMParser and manual mapping for reliability.
 *
 * @param text - Text containing HTML entities (e.g., "&#039;" or "&quot;")
 * @returns Decoded text with actual characters (e.g., "'" or '"')
 */
export const decodeHtmlEntities = (text: string): string => {
  // First, handle common named entities manually for reliability
  const entityMap: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#039;': "'",
    '&#39;': "'",
    '&apos;': "'",
  };

  let decoded = text;

  // Replace named entities
  Object.entries(entityMap).forEach(([entity, char]) => {
    decoded = decoded.split(entity).join(char);
  });

  // Handle numeric entities (&#NNN; format) using textarea trick
  if (decoded.includes('&#')) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = decoded;
    decoded = textarea.value;
  }

  return decoded;
};

/**
 * Escapes HTML special characters to prevent XSS attacks.
 * NOTE: We don't escape single quotes (') or double quotes (") because:
 * 1. We're not inserting into HTML attributes (we use React props)
 * 2. We've already decoded HTML entities, so re-escaping would break the display
 * 3. XSS protection is handled by React's automatic escaping
 */
const escapeHtml = (text: string): string => {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
  };
  return text.replace(/[&<>]/g, (char) => map[char]);
};

/**
 * Renders simple markdown formatting (bold text with **) as React elements
 * WITHOUT using dangerouslySetInnerHTML.
 *
 * Supports:
 * - **bold text** → <strong>bold text</strong>
 * - Decodes HTML entities (e.g., &#039; → ')
 *
 * SECURITY: All content is HTML-escaped to prevent XSS attacks
 * SECURITY: Uses sequential counter for keys to prevent manipulation
 *
 * @param text - The text with markdown formatting (may contain HTML entities)
 * @returns Array of React elements (strings and <strong> tags)
 */
export function renderSimpleMarkdown(text: string): React.ReactNode[] {
  // First decode any HTML entities that may be present in the input
  const decodedText = decodeHtmlEntities(text);

  const parts: React.ReactNode[] = [];
  let currentIndex = 0;
  let keyCounter = 0;

  const boldPattern = /\*\*([^*]+)\*\*/g;
  let match: RegExpExecArray | null;

  while ((match = boldPattern.exec(decodedText)) !== null) {
    if (match.index > currentIndex) {
      parts.push(escapeHtml(decodedText.substring(currentIndex, match.index)));
    }

    parts.push(<strong key={`bold-${keyCounter++}`}>{escapeHtml(match[1])}</strong>);

    currentIndex = match.index + match[0].length;
  }

  if (currentIndex < decodedText.length) {
    parts.push(escapeHtml(decodedText.substring(currentIndex)));
  }

  return parts.length > 0 ? parts : [decodedText];
}
